import { Color, Palette } from '@mui/material';
import { type PaletteColor, type PaletteOptions, createTheme } from '@mui/material/styles';

import { buildThemeComponents, buildThemeTypography } from './themeOverrides';

interface AccentExtension {
  header: string;
  medium: string;
  sidemenu: string;
}

interface ScrollbarColors {
  thumb: string;
  track: string;
}

declare module '@mui/material/Paper' {
  interface ButtonPropsVariantOverrides {
    dashed: true;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    blue: Color;
    purple: Color;
    red: Color;
    green: Color;
    orange: Color;
    magenta: Color;
    accent: PaletteColor;
    accentExtension: AccentExtension;
    scrollbar: ScrollbarColors;
  }
  interface PaletteOptions {
    blue?: Partial<Color>;
    purple?: Partial<Color>;
    red?: Partial<Color>;
    green?: Partial<Color>;
    orange?: Partial<Color>;
    magenta?: Partial<Color>;
    accent?: Partial<PaletteColor>;
    accentExtension?: Partial<AccentExtension>;
    scrollbar?: Partial<ScrollbarColors>;
  }
}

declare module '@mui/material' {
  interface Color {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950?: string;
    A100: string;
    A200: string;
    A400: string;
    A700: string;
  }
}

const getTheme = (userType: 'backoffice' | 'location' | 'advertiser' | 'service' | undefined) => {
  let colors = {
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    primary: {
      main: 'rgba(69, 146, 250, 1)',
      dark: 'rgba(69, 146, 250, 0.3)',
      light: 'rgba(69, 146, 250, 0.1)',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#8F8EA3',
      dark: '#504E5A',
      light: '#F5F5F7',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#AC9AEB',
      dark: '#7F52D5',
      light: '#F6F5FD',
      contrastText: '#FFF',
    },
    success: {
      main: '#31B77A',
      dark: '#237452',
      light: '#F7FCF9',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: 'rgba(255, 177, 71, 1)',
      dark: '#C1450A',
      light: '#FFFBF0',
      contrastText: '#FFF',
    },
    error: {
      main: '#E9554A',
      dark: '#95271F',
      light: '#FEF3F2',
      contrastText: '#FFF',
    },
    grey: {
      50: '#F5F5F7',
      100: '#EBEBEE',
      200: '#DBDBE2',
      300: '#C2C3CE',
      400: '#A4A4B6',
      500: '#8F8EA3',
      600: '#807D93',
      700: '#737085',
      800: '#615E6F',
      900: '#504E5A',
      950: '#333239',
    },
    blue: {
      50: '#EFF7FF',
      100: '#DAECFF',
      200: '#BEDEFF',
      300: '#91CAFF',
      400: '#5DACFD',
      500: '#4592FA',
      600: '#216BEF',
      700: '#1955DC',
      800: '#1B46B2',
      900: '#1C3E8C',
      950: '#162755',
    },
    purple: {
      50: '#F6F5FD',
      100: '#EFECFB',
      200: '#DFDBF9',
      300: '#C9BEF4',
      400: '#AC9AEB',
      500: '#8E6DE1',
      600: '#7F52D5',
      700: '#6F3FC2',
      800: '#5D35A2',
      900: '#4D2D85',
      950: '#301B5A',
    },
    red: {
      50: '#FEF3F2',
      100: '#FDE5E3',
      200: '#FCCFCC',
      300: '#F9AEA8',
      400: '#F3786F',
      500: '#E9554A',
      600: '#D6382C',
      700: '#B42B21',
      800: '#95271F',
      900: '#7C2620',
      950: '#43100C',
    },
    green: {
      50: '#F7FCF9',
      100: '#ECF8F0',
      200: '#DBF1E3',
      300: '#C3E6D3',
      400: '#B5DCC8',
      500: '#62CB99',
      600: '#31B77A',
      700: '#279265',
      800: '#237452',
      900: '#1E6045',
      950: '#0F3527',
    },
    orange: {
      50: '#FFFBF0',
      100: '#FFF5D5',
      200: '#FFE8A7',
      300: '#FFD876',
      400: '#FFC959',
      500: '#FFB147',
      600: '#FF881A',
      700: '#EF5C02',
      800: '#C1450A',
      900: '#A03A0C',
      950: '#5C1C00',
    },
    magenta: {
      50: '#FCF3FA',
      100: '#FAE9F7',
      200: '#F7D3F0',
      300: '#F2AFE2',
      400: '#E97DCE',
      500: '#DE56B7',
      600: '#CC369A',
      700: '#A82478',
      800: '#922268',
      900: '#7A2159',
      950: '#4A0D32',
    },
    accent: {
      main: '',
      dark: '',
      light: '',
      contrastText: '',
    },
    accentExtension: {
      header: '',
      medium: '',
    },
    scrollbar: {
      thumb: '',
      track: '',
    },
  };

  let main;
  let dark;
  let light;
  let contrastText;
  let accentExtension: AccentExtension;

  switch (userType) {
    case 'location':
      main = colors.purple[500];
      dark = colors.purple[800];
      light = colors.purple[100];
      accentExtension = {
        header: colors.purple[200],
        medium: colors.purple[400],
        sidemenu: colors.purple[50],
      };
      contrastText = '#FFFFFF';
      break;
    case 'advertiser':
      main = colors.magenta[500];
      dark = colors.magenta[800];
      light = colors.magenta[100];
      accentExtension = {
        header: colors.magenta[200],
        medium: colors.magenta[400],
        sidemenu: colors.magenta[50],
      };
      contrastText = '#FFFFFF';
      break;
    case 'service':
      main = colors.blue[500];
      dark = colors.blue[800];
      light = colors.blue[100];
      accentExtension = {
        header: colors.blue[200],
        medium: colors.blue[400],
        sidemenu: colors.blue[50],
      };
      contrastText = '#FFFFFF';
      colors.background.default = '#F7F8FC';
      break;
    default:
      main = colors.orange[500];
      dark = colors.orange[800];
      light = colors.orange[50];
      accentExtension = {
        header: colors.orange[200],
        medium: colors.orange[400],
        sidemenu: colors.orange[50],
      };
      contrastText = '#FFFFFF';
  }

  colors = {
    ...colors,
    accent: {
      main,
      dark,
      light,
      contrastText,
    },
    accentExtension,
    scrollbar: {
      thumb: '#808080',
      track: 'transparent',
    },
  };

  const baseTheme = createTheme({ palette: colors as unknown as PaletteOptions });
  const palette = baseTheme.palette as Palette;

  return createTheme(baseTheme, {
    palette,
    typography: buildThemeTypography(palette),
    components: buildThemeComponents(palette),
  });
};

export default getTheme;
